/**
 * Competitor comparison data — used by /vs/:vendor SEO landing pages.
 *
 * Honesty rule: every claim must be verifiable. If we don't know what a
 * competitor charges, we say "see their pricing page". If a competitor does
 * something better than we do, we say so. The goal is credible comparison,
 * not vaporware marketing — prospects sniff out exaggeration in 5 seconds.
 *
 * Each entry maps a vendor slug → page-render config.
 */

export const COMPETITORS = {
  datadog: {
    slug: 'datadog',
    name: 'Datadog',
    category: 'Observability + LLM Monitoring',
    h1: 'CARTIEAI vs Datadog: which one cuts your cloud + AI bill?',
    subhead:
      'Datadog is the gold standard for observability — but it was built to watch your systems, not to lower the bill. Here is the honest side-by-side, with no marketing spin.',
    summary:
      'Use Datadog if you need world-class APM + logs + traces with optional LLM observability bolted on. Use CARTIEAI if your priority is cutting cloud + AI token spend by 20-40% without adding observability cost on top.',
    when_to_pick_them: [
      'Your team already runs APM/logs/traces and you want unified observability.',
      'You need request-level LLM tracing tied to user sessions.',
      'Budget is not your primary constraint — observability quality is.',
    ],
    when_to_pick_us: [
      'Your CFO is asking why the AI bill grew 4× this quarter.',
      'You want per-feature P&L (margin per `summarise`, per `chat`, etc.).',
      'You need PR-time cost prediction before infra hits production.',
      'You want to ship in 5 minutes, not 5 weeks.',
    ],
    pricing: {
      them: 'Custom enterprise pricing. Public reports suggest $15-23/host/mo for APM, plus LLM Observability as a separate SKU. Most teams report $80K-$500K+ ARR.',
      us: 'Free tier (real features, not a sandbox). Pro at $199/mo. Outcome-based pricing for enterprise — you only pay if we save you money.',
    },
    comparison: [
      { capability: 'AWS / Azure / GCP cost rollups', them: 'partial', us: 'full' },
      { capability: 'Snowflake + Databricks + AI token costs unified', them: 'no', us: 'world-class' },
      { capability: 'Per-feature AI margin (P&L per `feature_slug`)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction (terraform plan → $ delta)', them: 'no', us: 'world-class' },
      { capability: 'Multi-model smart router middleware (drop-in Python + JS)', them: 'no', us: 'world-class' },
      { capability: 'Anomaly detection across 6 cloud + AI providers', them: 'partial', us: 'yes' },
      { capability: 'Boardroom-ready PDF export per customer P&L', them: 'no', us: 'world-class' },
      { capability: 'Outcome-based pricing (only pay if we save you money)', them: 'no', us: 'yes' },
      { capability: 'Full-stack APM + distributed tracing + RUM', them: 'world-class', us: 'no' },
      { capability: 'Real-user monitoring + session replay', them: 'world-class', us: 'no' },
      { capability: 'Log search at 100B+ events/day', them: 'world-class', us: 'no' },
    ],
    scope_clarification: {
      title: 'We do not compete on APM, RUM, or log search — by design',
      body:
        "Datadog is the gold standard for full-stack observability. Building a credible APM/RUM/log-search product is a multi-hundred-engineer, multi-year investment — and the moment you try, you stop being a focused cost-cutting tool.",
      truth:
        "CARTIEAI is a FinOps tool. We make the cloud + AI bill go down. Datadog is a watchtower. It tells you which span took 4.2s.",
      recommendation:
        "If you already run Datadog and like it: keep it. Pair CARTIEAI on top to translate your $20K/mo Datadog bill into per-customer P&L. If you do not yet run Datadog and need APM: pick Datadog. Then come to us when the Datadog bill itself becomes a problem.",
    },
    keywords:
      'datadog vs cartie ai, datadog finops alternative, datadog llm observability pricing, datadog cost optimization, datadog cloud cost management',
  },

  helicone: {
    slug: 'helicone',
    name: 'Helicone',
    category: 'LLM Observability',
    h1: 'CARTIEAI vs Helicone: token observability vs full-stack AI FinOps',
    subhead:
      'Helicone is a great LLM proxy + observability tool — it shows you every prompt, response, and cost. CARTIEAI takes that data and turns it into per-customer P&L, smart routing, and CFO-ready reports.',
    summary:
      'Use Helicone if you need a developer-first LLM proxy with per-request observability and rate-limiting. Use CARTIEAI if you need to answer "is feature X profitable?" or "which model should we move chat to?" with deterministic math.',
    when_to_pick_them: [
      'You want a proxy in front of OpenAI / Anthropic / Together that logs every call.',
      'Your team needs per-user rate-limits enforced at the LLM layer.',
      'You want prompt versioning + caching at the proxy.',
    ],
    when_to_pick_us: [
      'You need to answer "is `summarise` profitable per customer?".',
      'You need PR-time + Terraform-plan cost prediction across all infra (not just LLMs).',
      'You want unified cloud + AI cost rollups, not just LLM observability.',
      'You want a smart-router that emits drop-in middleware code.',
    ],
    pricing: {
      them: 'Free tier with 10K requests/mo. Pro at $20/seat/mo. Growth $200+/mo by request volume. Enterprise custom.',
      us: 'Free tier for the public free tools (PR-cost, Prompt-cost predictor, Bill Score). Pro at $199/mo flat. Outcome-based for enterprise.',
    },
    comparison: [
      { capability: 'LLM proxy + per-request logs', them: 'world-class', us: 'yes' },
      { capability: 'Prompt versioning + experiments', them: 'world-class', us: 'yes' },
      { capability: 'Per-feature P&L (revenue minus token cost)', them: 'no', us: 'world-class' },
      { capability: 'Per-customer token heatmap', them: 'no', us: 'world-class' },
      { capability: 'Multi-model smart router with code generation', them: 'no', us: 'world-class' },
      { capability: 'Context window optimizer (LLM-powered)', them: 'no', us: 'world-class' },
      { capability: 'Cache-miss detector (Anthropic prompt caching eligibility)', them: 'no', us: 'world-class' },
      { capability: 'AWS / Azure / GCP / Snowflake / Databricks integration', them: 'no', us: 'yes' },
      { capability: 'PR-time cost prediction for Terraform plans', them: 'no', us: 'world-class' },
      { capability: 'Decision Simulator (rank cost-saving scenarios)', them: 'no', us: 'world-class' },
    ],
    keywords:
      'helicone vs cartie ai, helicone alternative, llm observability finops, helicone pricing, ai cost monitoring',
  },

  cloudability: {
    slug: 'cloudability',
    name: 'Apptio Cloudability',
    category: 'Enterprise Cloud FinOps',
    h1: 'CARTIEAI vs Apptio Cloudability: do you need an enterprise FinOps tool or a tighter loop?',
    subhead:
      'Cloudability is the FinOps Foundation reference architecture — it covers AWS, Azure, GCP at IBM-grade depth. CARTIEAI is the lighter, AI-native, outcome-priced challenger built for product-led teams.',
    summary:
      'Use Cloudability if you are a Fortune 500 with a dedicated FinOps team and 6-month procurement cycles. Use CARTIEAI if you are a 5-500 person product company that wants to start saving in week 1, not quarter 2.',
    when_to_pick_them: [
      'You have a dedicated FinOps practice and need FOCUS 1.1 enterprise rollouts.',
      'You need decade-old Anomaly Service Catalog parity for procurement-mandated tooling.',
      'You can absorb $50K-$150K/yr in tooling cost AND 3-month onboarding.',
    ],
    when_to_pick_us: [
      'You want AI feature P&L, which IBM has not shipped.',
      'You want PR-time cost prediction, which IBM has not shipped.',
      'You want flat $199/mo OR outcome-based pricing — not a 12-week sales cycle.',
      'You want token-level + cloud-level cost in one product, not two.',
    ],
    pricing: {
      them: 'Custom enterprise pricing. Public reports suggest $50K-$200K+ ARR, typically gated by spend size (often 2-3% of cloud spend).',
      us: 'Flat $199/mo Pro. Outcome-based enterprise (% of verified savings, capped).',
    },
    comparison: [
      { capability: 'AWS / Azure / GCP unified cost', them: 'world-class', us: 'yes' },
      { capability: 'FOCUS 1.1 export', them: 'yes', us: 'yes' },
      { capability: 'Commitment-buy recommendations (SP / RI / CUD)', them: 'yes', us: 'yes' },
      { capability: 'Per-feature AI margin (P&L per `feature_slug`)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction', them: 'no', us: 'world-class' },
      { capability: 'Token Intelligence Suite (10 AI-cost features)', them: 'no', us: 'world-class' },
      { capability: 'Customer P&L from Stripe + cloud join', them: 'no', us: 'world-class' },
      { capability: 'Decision Simulator (rank scenarios)', them: 'partial', us: 'yes' },
      { capability: 'Outcome-based pricing', them: 'no', us: 'yes' },
      { capability: 'Flat $199/mo SMB tier', them: 'no', us: 'yes' },
      { capability: '5-minute setup', them: 'no', us: 'yes' },
    ],
    keywords:
      'cloudability vs cartie ai, apptio cloudability alternative, finops platform comparison, cloudability pricing, finops smb',
  },

  langsmith: {
    slug: 'langsmith',
    name: 'LangSmith',
    category: 'LangChain Observability',
    h1: 'CARTIEAI vs LangSmith: build-side tracing vs run-side FinOps',
    subhead:
      'LangSmith is the official observability tool for LangChain apps — it traces every chain, agent, and tool call. CARTIEAI lives one layer down: turning that trace data into dollars, margins, and routing decisions.',
    summary:
      'Use LangSmith if your stack is LangChain-heavy and you need chain-level debugging + evals. Use CARTIEAI alongside it (or instead) when the conversation shifts from "is this chain correct?" to "is this chain profitable?".',
    when_to_pick_them: [
      'You build agents with LangChain / LangGraph and need step-level traces.',
      'You run evals + A/B tests on chain outputs at scale.',
      'You need a single source-of-truth for chain debugging across the org.',
    ],
    when_to_pick_us: [
      'You want LangSmith-class trace capture + evals AND cloud cost in one tool.',
      'Your CFO wants per-customer profitability of each AI feature.',
      'You want a smart-router that ships drop-in routing code.',
      'You want PR-time + commit-time cost guards.',
      'Your stack is not LangChain-only (mixed OpenAI + Anthropic + native).',
    ],
    pricing: {
      them: 'Free tier 5K traces/mo. Plus $39/seat/mo + usage. Enterprise custom.',
      us: 'Free public tools. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'LangChain / LangGraph step-level tracing', them: 'world-class', us: 'yes' },
      { capability: 'Chain-output evals + A/B tests', them: 'world-class', us: 'yes' },
      { capability: 'Prompt playground + versioning', them: 'world-class', us: 'yes' },
      { capability: 'Per-feature P&L (margin per AI feature)', them: 'no', us: 'world-class' },
      { capability: 'Per-customer token heatmap', them: 'no', us: 'world-class' },
      { capability: 'Multi-model smart router with middleware code', them: 'no', us: 'world-class' },
      { capability: 'Cache-miss eligibility detector', them: 'no', us: 'world-class' },
      { capability: 'AWS / Azure / GCP / Snowflake / Databricks cost', them: 'no', us: 'yes' },
      { capability: 'PR-time cost prediction for infra', them: 'no', us: 'world-class' },
      { capability: 'Customer P&L (Stripe + cloud cost join)', them: 'no', us: 'world-class' },
    ],
    keywords:
      'langsmith vs cartie ai, langsmith alternative, langchain cost tracking, langsmith pricing, ai cost observability',
  },

  vantage: {
    slug: 'vantage',
    name: 'Vantage',
    category: 'Modern Cloud Cost Management',
    h1: 'CARTIEAI vs Vantage: cost reporting vs taking action',
    subhead:
      'Vantage is a beautifully-designed cloud cost dashboard for AWS, Azure, GCP and 20+ other SaaS providers. CARTIEAI goes one step further: AI feature P&L, PR-time cost guards, and outcome-based pricing.',
    summary:
      'Use Vantage if you want a clean dashboard across many cost sources and your team will action insights manually. Use CARTIEAI if you want the platform to take action — flag PRs, route models, project savings, and auto-broadcast wins.',
    when_to_pick_them: [
      'You want broad SaaS cost integrations (Datadog, Snowflake, Confluent, etc.) in one dashboard.',
      'Your team has a FinOps practitioner who manually drives optimization.',
      'You want simple per-team / per-tag chargeback views.',
    ],
    when_to_pick_us: [
      'You want PR-time cost prediction (Terraform plan → $ delta).',
      'You want AI feature P&L (margin per `summarise`, per `chat`).',
      'You want a smart-router that ships drop-in middleware code.',
      'You want outcome-based pricing — pay only if we save you money.',
    ],
    pricing: {
      them: 'Free tier up to $2.5K/mo spend. Team $250+/mo. Business $1,200+/mo. Enterprise custom.',
      us: 'Free tier with real features. Pro $199/mo. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'AWS / Azure / GCP cost dashboard', them: 'world-class', us: 'yes' },
      { capability: '20+ SaaS provider integrations (Datadog, MongoDB Atlas, Vercel, Cloudflare, Confluent…)', them: 'yes', us: 'yes' },
      { capability: 'Per-team / per-tag chargeback views', them: 'yes', us: 'yes' },
      { capability: 'AI feature P&L (margin per `feature_slug`)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction (Terraform plan → $ delta)', them: 'no', us: 'world-class' },
      { capability: 'Token Intelligence Suite (10 AI cost features)', them: 'no', us: 'world-class' },
      { capability: 'Multi-model smart router middleware', them: 'no', us: 'world-class' },
      { capability: 'Customer P&L from Stripe + cloud join', them: 'no', us: 'world-class' },
      { capability: 'Decision Simulator (rank cost scenarios)', them: 'no', us: 'world-class' },
      { capability: 'Outcome-based pricing', them: 'no', us: 'yes' },
    ],
    keywords:
      'vantage vs cartie ai, vantage alternative, cloud cost dashboard comparison, vantage pricing, cloud finops smb',
  },

  cloudzero: {
    slug: 'cloudzero',
    name: 'CloudZero',
    category: 'Cloud Cost Intelligence',
    h1: 'CARTIEAI vs CloudZero: unit-cost visibility vs the full savings loop',
    subhead:
      'CloudZero pioneered cost-per-customer and cost-per-feature intelligence — genuinely great unit economics. CARTIEAI covers that ground AND closes the loop: detect, cut automatically, prove the savings against the real bill.',
    summary:
      'Use CloudZero if you want best-in-class unit-cost visibility with a dedicated FinOps analyst culture. Use CARTIEAI if you also want token-level AI engines, PR-time prevention, and automation that acts on what it finds.',
    when_to_pick_them: [
      'You want mature cost-per-unit telemetry (cost per customer / feature / sprint) with years of enterprise references.',
      'You have a FinOps analyst team that will drive optimization manually from the insights.',
      'Procurement prefers an established vendor with a long enterprise track record.',
    ],
    when_to_pick_us: [
      'You want the 10-feature Token Intelligence Suite (leak detection, cache-miss, smart routing) — not just cost rollups.',
      'You want PR-time cost prediction and merge gates, not post-invoice analysis.',
      'You want conversion-aware ROI (cost ↔ revenue join with PROFITABLE / KILL verdicts).',
      'You want flat $199/mo or outcome-based pricing instead of enterprise-only contracts.',
    ],
    pricing: {
      them: 'Custom enterprise pricing tied to cloud spend — public reports commonly cite $30K+/yr entry points. See their pricing page.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Cost per customer / feature / unit metric', them: 'world-class', us: 'yes' },
      { capability: 'AWS / Azure / GCP + K8s cost unification', them: 'world-class', us: 'yes' },
      { capability: 'Token-level LLM cost engines (leaks, cache-miss, routing)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction (Terraform plan → $ delta)', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'partial', us: 'world-class' },
      { capability: 'Automated remediation with human approval', them: 'no', us: 'yes' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
      { capability: 'Anomaly alerting', them: 'yes', us: 'yes' },
      { capability: 'Flat SMB pricing tier', them: 'no', us: 'yes' },
    ],
    keywords:
      'cloudzero vs cartie ai, cloudzero alternative, cost per customer, unit cost economics, cloudzero pricing, cloud cost intelligence',
  },

  castai: {
    slug: 'castai',
    name: 'CAST AI',
    category: 'Kubernetes Automation',
    h1: 'CARTIEAI vs CAST AI: K8s compute automation vs whole-bill FinOps',
    subhead:
      'CAST AI is genuinely excellent at one thing: automating Kubernetes compute — autoscaling, spot lifecycle, bin-packing, even live migration. CARTIEAI optimizes the whole bill: LLM tokens, cloud services, K8s fairness, and the revenue side.',
    summary:
      'Use CAST AI if K8s compute is your dominant cost and you want hands-off node/pod automation. Use CARTIEAI if your spend spans AI tokens + cloud services + K8s, and you need attribution, causality, and proof — not just infra tuning.',
    when_to_pick_them: [
      'K8s compute is 70%+ of your bill and you want automated rightsizing / spot management today.',
      'You want container live-migration for stateful workloads across nodes.',
      'Your pain is cluster efficiency, not cost attribution or AI spend.',
    ],
    when_to_pick_us: [
      'Your AI token bill is growing faster than your compute bill.',
      'You need fair shared-cluster chargeback (Shapley attribution), not just cheaper nodes.',
      'You want commit-level causality: which PR made the bill jump.',
      'You want one platform for tokens + cloud + K8s + revenue join.',
    ],
    pricing: {
      them: 'Free cost monitoring tier. Automation priced per managed CPU — custom quotes. See their pricing page.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'K8s node autoscaling + bin-packing automation', them: 'world-class', us: 'no' },
      { capability: 'Spot instance lifecycle automation (fallback, diversity)', them: 'world-class', us: 'partial' },
      { capability: 'GPU spot bid-price optimization (checkpoint-aware math)', them: 'partial', us: 'world-class' },
      { capability: 'Fair shared-cluster chargeback (Shapley attribution)', them: 'no', us: 'world-class' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'partial', us: 'world-class' },
      { capability: 'AWS / Azure / GCP service-level cost (beyond K8s)', them: 'partial', us: 'yes' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'partial', us: 'world-class' },
    ],
    scope_clarification: {
      title: 'We do not compete on live K8s infrastructure automation — by design',
      body:
        'CAST AI moves pods, resizes nodes, and live-migrates stateful workloads in production clusters. That is deep infrastructure-orchestration engineering, and they do it very well.',
      truth:
        'CARTIEAI answers a different question: not "how do I pack this cluster tighter?" but "where is every dollar going, who caused it, and is it earning revenue?"',
      recommendation:
        'If K8s compute dominates your bill, run CAST AI for cluster automation — and pair CARTIEAI on top for token spend, fair chargeback, commit causality, and the revenue join. The two are complementary, not substitutes.',
    },
    keywords:
      'cast ai vs cartie ai, cast ai alternative, kubernetes cost optimization, k8s automation comparison, cast ai pricing',
  },

  truefoundry: {
    slug: 'truefoundry',
    name: 'TrueFoundry',
    category: 'AI Gateway + LLMOps',
    h1: 'CARTIEAI vs TrueFoundry: the AI gateway vs the AI FinOps loop',
    subhead:
      'TrueFoundry is a strong enterprise AI gateway — unified access to 1,000+ models, guardrails, RBAC, and on-prem / air-gapped deployment. CARTIEAI is the FinOps layer: finding waste, cutting it, and proving dollars saved across AI AND cloud.',
    summary:
      'Use TrueFoundry if you need a governed, low-latency LLM gateway for a regulated enterprise. Use CARTIEAI if the question is "where is the AI + cloud money going, and how do we get it back?"',
    when_to_pick_them: [
      'You need an enterprise gateway: RBAC, guardrails, key management, geo-routing at 3-4ms overhead.',
      'You must deploy fully on-prem or air-gapped today (banking / defense grade).',
      'You want prompt lifecycle management + an agent playground for builders.',
    ],
    when_to_pick_us: [
      'You want waste detection engines, not just routing: token leaks, cache-miss, retry storms, output bloat.',
      'You need cloud-bill FinOps (AWS / Azure / GCP) in the same platform as AI spend.',
      'You want commit-level causality and PR-time cost gates wired to GitHub.',
      'You want conversion-aware ROI — is each AI feature actually profitable?',
    ],
    pricing: {
      them: 'Enterprise custom pricing. See their pricing page.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Enterprise AI gateway (1,000+ models, RBAC, guardrails)', them: 'world-class', us: 'partial' },
      { capability: 'On-prem / air-gapped deployment', them: 'world-class', us: 'no' },
      { capability: 'Token cost attribution per user / team / model', them: 'yes', us: 'yes' },
      { capability: 'Waste-hunting engines (leaks, cache-miss, retry storms, output diet)', them: 'no', us: 'world-class' },
      { capability: 'AWS / Azure / GCP cloud-bill FinOps', them: 'no', us: 'yes' },
      { capability: 'Per-feature AI P&L (margin per feature_slug)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
    ],
    scope_clarification: {
      title: 'They deploy on-prem today. We do not — yet.',
      body:
        'TrueFoundry ships in customer VPCs, on-prem, and air-gapped environments. Our self-hosted sidecar is on the roadmap, not in the product. If that is your hard requirement today, they win that row — honestly.',
      truth:
        'A gateway routes and governs traffic. A FinOps loop finds the waste, cuts it, and proves the dollars. Different jobs.',
      recommendation:
        'Regulated enterprise needing air-gapped LLM access now: pick TrueFoundry. Product teams whose AI + cloud bill is the pain: pick CARTIEAI. Running both is coherent — gateway underneath, FinOps loop on top.',
    },
    keywords:
      'truefoundry vs cartie ai, truefoundry alternative, ai gateway comparison, llm gateway finops, truefoundry pricing',
  },

  amnic: {
    slug: 'amnic',
    name: 'Amnic',
    category: 'Cloud Cost Observability',
    h1: 'CARTIEAI vs Amnic: cost observability agents vs the savings loop',
    subhead:
      'Amnic is a solid multi-cloud cost observability platform with four AI agents and transparent pricing. CARTIEAI shares the transparency ethos — and goes further on AI-token depth, prevention, and proving realized savings.',
    summary:
      'Use Amnic if you want agentless, read-only cost observability across many clouds (including Oracle and Alibaba). Use CARTIEAI if you want deep LLM-token engines, PR-time prevention, and a verified savings ledger — not just visibility.',
    when_to_pick_them: [
      'You run Oracle Cloud or Alibaba Cloud alongside the big three — their cloud coverage is broader.',
      'You want strictly read-only, agentless observability with no write access.',
      'You want conversational AI agents over your cost data with published tiered pricing.',
    ],
    when_to_pick_us: [
      'AI token spend is a first-class problem — you need the 10-feature Token Intelligence Suite, not token tracking that is still rolling out.',
      'You want prevention (PR gates) and action (approved auto-fixes), not read-only reports.',
      'You want commit-level causality and conversion-aware ROI.',
      'You want savings verified against the actual bill, not estimated.',
    ],
    pricing: {
      them: 'Published tiers from $99/mo (up to $10K tracked spend) to $9,999/mo (up to $1.5M). Enterprise at 0.25-1% of monitored spend.',
      us: 'Free tier with real features. Pro $199/mo flat — not scaled to your spend. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Multi-cloud coverage (incl. Oracle + Alibaba)', them: 'world-class', us: 'partial' },
      { capability: 'K8s node / container-level visibility', them: 'yes', us: 'yes' },
      { capability: 'Conversational AI agents over cost data', them: 'yes', us: 'yes' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'partial', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Automated remediation with human approval', them: 'no', us: 'yes' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
      { capability: 'Transparent public pricing', them: 'yes', us: 'yes' },
    ],
    keywords:
      'amnic vs cartie ai, amnic alternative, cloud cost observability comparison, amnic pricing, multi-cloud cost tool',
  },

  mavvrik: {
    slug: 'mavvrik',
    name: 'Mavvrik',
    category: 'AI Cost Governance',
    h1: 'CARTIEAI vs Mavvrik: AI cost governance vs the AI savings loop',
    subhead:
      'Mavvrik is an AI-native cost governance platform strong on hybrid / on-prem GPU chargeback and agent-level tracking. CARTIEAI is the loop that acts: detect the waste, cut it automatically, prove every dollar.',
    summary:
      'Use Mavvrik if you run large on-prem GPU clusters and need enterprise chargeback governance. Use CARTIEAI if you want waste actively hunted and cut — with PR gates, smart routing, and a verified savings ledger — at a price a product team can expense.',
    when_to_pick_them: [
      'You operate hybrid / on-prem GPU clusters and need unified chargeback across them — they cover on-prem, we do not.',
      'You want OpenTelemetry-based agent-level cost tracking for multi-agent workflows.',
      'You are an enterprise buying through direct sales / GCP Marketplace and want persona dashboards (CTO / CFO / SRE).',
    ],
    when_to_pick_us: [
      'You want engines that find and cut waste (leaks, cache-miss, routing, output diet), not governance dashboards alone.',
      'You want PR-time prevention and commit-level causality wired to GitHub.',
      'You want conversion-aware ROI — which AI features earn their keep.',
      'You want public pricing and a 60-second self-serve trial, not a sales cycle.',
    ],
    pricing: {
      them: 'No public pricing — enterprise plans scoped to cloud / AI footprint via direct sales.',
      us: 'Free tier with real features. Pro $199/mo flat, publicly listed. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Hybrid / on-prem GPU cost coverage', them: 'world-class', us: 'no' },
      { capability: 'Agent-level cost tracking (OTel SDK)', them: 'yes', us: 'yes' },
      { capability: 'Token-level spend tracking across providers', them: 'yes', us: 'yes' },
      { capability: 'Waste-hunting engines (leaks, cache-miss, retry storms, output diet)', them: 'no', us: 'world-class' },
      { capability: 'Smart model routing with drop-in middleware', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'partial', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
      { capability: 'Self-serve trial + public pricing', them: 'no', us: 'yes' },
    ],
    keywords:
      'mavvrik vs cartie ai, mavvrik alternative, ai cost governance comparison, gpu chargeback, mavvrik pricing',
  },

  cloudhealth: {
    slug: 'cloudhealth',
    name: 'CloudHealth',
    category: 'Enterprise Cloud Cost Management',
    h1: 'CARTIEAI vs CloudHealth: enterprise governance vs the savings loop',
    subhead:
      'CloudHealth (VMware, now under Broadcom) is a mature enterprise cost-governance platform with a deep MSP ecosystem. CARTIEAI is the AI-era loop: token intelligence, commit causality, and savings proven against the bill.',
    summary:
      'Use CloudHealth if you are a large enterprise or MSP needing multi-cloud governance policies and mature reporting. Use CARTIEAI if AI spend, engineering-level causality, and automated waste-cutting are the problems on your desk.',
    when_to_pick_them: [
      'You are an MSP managing dozens of client cloud accounts — their partner tooling is the industry standard.',
      'You need mature governance policies, budgets, and enterprise reporting with a long track record.',
      'Procurement wants an established enterprise vendor relationship.',
    ],
    when_to_pick_us: [
      'Your AI/LLM bill is growing and CloudHealth has no token-level visibility at all.',
      'You want to know which commit or PR caused a spike, not just which service.',
      'You want automated remediation and PR-time prevention, not policy reports.',
      'You want transparent pricing without an enterprise sales cycle.',
    ],
    pricing: {
      them: 'Enterprise custom pricing (typically tied to % of cloud spend) via Broadcom/partner sales.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Multi-cloud governance policies + enterprise reporting', them: 'world-class', us: 'partial' },
      { capability: 'MSP / partner multi-account management', them: 'world-class', us: 'no' },
      { capability: 'Rightsizing + RI/commitment recommendations', them: 'yes', us: 'yes' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Automated remediation with human approval', them: 'partial', us: 'yes' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
      { capability: 'Self-serve trial + public pricing', them: 'no', us: 'yes' },
    ],
    keywords:
      'cloudhealth vs cartie ai, cloudhealth alternative, vmware cloudhealth comparison, enterprise cloud cost governance',
  },

  kubecost: {
    slug: 'kubecost',
    name: 'Kubecost',
    category: 'Kubernetes Cost Monitoring',
    h1: 'CARTIEAI vs Kubecost: K8s cost visibility vs fair attribution + the loop',
    subhead:
      'Kubecost (now part of IBM/Apptio) is the de-facto standard for Kubernetes cost allocation — genuinely great at what it does. CARTIEAI covers K8s too, then goes where Kubecost stops: fair Shapley chargeback, AI token spend, and proof.',
    summary:
      'Use Kubecost if you want free, self-hosted, real-time K8s cost allocation today. Use CARTIEAI if you need provably fair shared-cost splits, the rest of your bill (LLM tokens + cloud services), and savings verified — not just observed.',
    when_to_pick_them: [
      'You want free, open-core, self-hosted K8s cost monitoring inside your cluster.',
      'Real-time per-namespace/pod allocation is your only requirement.',
      'You are already on IBM/Apptio tooling and want the bundled path.',
    ],
    when_to_pick_us: [
      'Proportional splits cause chargeback fights — our Shapley attribution is provably fair (2012 Nobel math).',
      'K8s is only part of your bill: we cover LLM tokens, cloud services, and SaaS in one loop.',
      'You want an engineer-hour payback gate on every recommendation, not a raw savings list.',
      'You want commit-level causality and PR gates wired to GitHub.',
    ],
    pricing: {
      them: 'Free open-source tier (self-hosted). Enterprise custom pricing via IBM.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Real-time K8s cost allocation (namespace / pod / label)', them: 'world-class', us: 'yes' },
      { capability: 'Self-hosted in-cluster deployment', them: 'world-class', us: 'no' },
      { capability: 'Idle / zombie K8s workload detection', them: 'yes', us: 'yes' },
      { capability: 'Provably fair shared-cost split (Shapley attribution)', them: 'no', us: 'world-class' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'no', us: 'world-class' },
      { capability: 'Cloud service costs beyond K8s (incl. SaaS)', them: 'partial', us: 'yes' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'no', us: 'world-class' },
    ],
    scope_clarification: {
      title: 'They live inside your cluster. We look at your whole bill.',
      body:
        'Kubecost runs self-hosted, in-cluster, with real-time granularity — that architecture is genuinely hard to beat for pure K8s observability, and we say so.',
      truth:
        'The chargeback question — "who pays for shared overhead?" — is a fairness problem, not an observability problem. Proportional splits punish small teams. Shapley values fix that.',
      recommendation:
        'Pure K8s shop with in-cluster requirements: Kubecost is a fine choice. Bill spanning tokens + cloud + K8s, with teams arguing over shared costs: that is what CARTIEAI is built for.',
    },
    keywords:
      'kubecost vs cartie ai, kubecost alternative, kubernetes cost allocation comparison, k8s chargeback shapley',
  },

  spotio: {
    slug: 'spotio',
    name: 'Spot.io',
    category: 'Compute Automation',
    h1: 'CARTIEAI vs Spot.io: spot-instance automation vs whole-bill FinOps',
    subhead:
      'Spot.io (Flexera) pioneered spot-instance automation — Ocean and Elastigroup are world-class at keeping workloads on cheap compute. CARTIEAI optimizes the entire bill: tokens, cloud services, K8s fairness, and revenue.',
    summary:
      'Use Spot.io if compute is your dominant cost and you want battle-tested spot lifecycle automation. Use CARTIEAI if your spend spans AI tokens + cloud + K8s and you need attribution, causality, and verified proof.',
    when_to_pick_them: [
      'Compute (EC2/VMs/containers) dominates your bill and spot savings are the top priority.',
      'You want mature, hands-off spot lifecycle automation with years of production references.',
      'You also use Flexera for ITAM/license management and want one vendor.',
    ],
    when_to_pick_us: [
      'Your LLM/AI token bill is the fastest-growing line item — spot automation cannot touch it.',
      'You need GPU spot bid-price optimization with checkpoint-aware interruption math.',
      'You want commit-level causality and PR-time prevention, not just infra efficiency.',
      'You want cost joined to revenue: which features earn their keep.',
    ],
    pricing: {
      them: 'Typically priced as a % of the savings generated (custom quotes via Flexera).',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Spot instance lifecycle automation (fallback, prediction)', them: 'world-class', us: 'partial' },
      { capability: 'Container / K8s compute automation (Ocean)', them: 'world-class', us: 'no' },
      { capability: 'Commitment management (RI/SP portfolio)', them: 'yes', us: 'yes' },
      { capability: 'GPU spot bid-price optimization (checkpoint-aware)', them: 'partial', us: 'world-class' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'no', us: 'world-class' },
      { capability: 'Fair shared-cluster chargeback (Shapley)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'partial', us: 'world-class' },
    ],
    keywords:
      'spot.io vs cartie ai, spot.io alternative, spot instance automation comparison, flexera spot pricing',
  },

  turbonomic: {
    slug: 'turbonomic',
    name: 'IBM Turbonomic',
    category: 'Application Resource Management',
    h1: 'CARTIEAI vs IBM Turbonomic: resource automation vs the AI savings loop',
    subhead:
      'Turbonomic is IBM\'s application resource management engine — automated rightsizing and placement across hybrid estates, tuned for performance. CARTIEAI is built for the AI era: token intelligence, cost causality, and dollars proven.',
    summary:
      'Use Turbonomic if you run a large hybrid/on-prem estate and want performance-driven resource automation. Use CARTIEAI if the question is where the AI + cloud money goes, who caused it, and how to get it back — with proof.',
    when_to_pick_them: [
      'You run significant on-prem/hybrid infrastructure (VMware, mainframe adjacency) — they cover it, we don\'t.',
      'Performance assurance (not just cost) drives your resourcing decisions.',
      'You are an IBM shop and want the bundled enterprise path.',
    ],
    when_to_pick_us: [
      'Your AI token spend is invisible to Turbonomic — it\'s a first-class citizen for us.',
      'You want commit-level causality: which PR broke the bill.',
      'You want cost joined to revenue and features graded PROFITABLE / KILL.',
      'You want a 60-second self-serve trial, not an enterprise deployment project.',
    ],
    pricing: {
      them: 'IBM enterprise licensing — custom quotes, typically annual contracts.',
      us: 'Free tier with real features. Pro $199/mo flat. Outcome-based enterprise.',
    },
    comparison: [
      { capability: 'Automated rightsizing + workload placement (hybrid/on-prem)', them: 'world-class', us: 'no' },
      { capability: 'Performance-aware resource management', them: 'world-class', us: 'partial' },
      { capability: 'K8s container rightsizing', them: 'yes', us: 'yes' },
      { capability: 'LLM token cost engines (10-feature suite)', them: 'no', us: 'world-class' },
      { capability: 'Fair shared-cluster chargeback (Shapley)', them: 'no', us: 'world-class' },
      { capability: 'PR-time cost prediction + merge gates', them: 'no', us: 'world-class' },
      { capability: 'Commit-level cost causality (Cost Bisect)', them: 'no', us: 'world-class' },
      { capability: 'Conversion-aware ROI (cost ↔ revenue join)', them: 'no', us: 'world-class' },
      { capability: 'Realized-savings verification vs actual bill', them: 'partial', us: 'world-class' },
      { capability: 'Self-serve trial + public pricing', them: 'no', us: 'yes' },
    ],
    keywords:
      'turbonomic vs cartie ai, turbonomic alternative, ibm turbonomic comparison, application resource management finops',
  },
};

export const COMPETITOR_SLUGS = Object.keys(COMPETITORS);
